// Logged-in user data-rights (DPDP) request surface.
// One entry point to submit access / correction / erasure / consent-withdrawal requests
// and view the status of past requests. Requests are logged and admin-fulfilled (v1 manual).
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ShieldCheck, Loader2, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REQUEST_TYPES = [
  { v: 'access', label: 'Access my data', help: 'Get a copy/summary of the personal data we hold about you.' },
  { v: 'correction', label: 'Correct my data', help: 'Ask us to fix or update inaccurate or incomplete data.' },
  { v: 'erasure', label: 'Delete my data', help: 'Request deletion of your data (some records may be retained where legally required).' },
  { v: 'consent_withdrawal', label: 'Withdraw consent', help: 'Withdraw a consent you previously gave. This may limit some services.' },
];

const CONSENT_OPTIONS = ['terms', 'privacy', 'intake', 'booking'];

const STATUS_COLORS = {
  open: 'bg-amber-50 text-amber-700 border-amber-200',
  in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
  resolved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
};

export default function PrivacyRequestsPage() {
  const navigate = useNavigate();
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  const [requestType, setRequestType] = useState('access');
  const [details, setDetails] = useState('');
  const [consentTypes, setConsentTypes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [policy, setPolicy] = useState(null);
  const [msg, setMsg] = useState(null);

  const fetchMine = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/privacy/requests/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setItems(data.items || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchMine();
    fetch(`${API_URL}/api/policies/versions`).then((r) => r.json()).then(setPolicy).catch(() => {});
  }, [fetchMine]);

  const toggleConsent = (c) => {
    setConsentTypes((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMsg(null);
    try {
      const res = await fetch(`${API_URL}/api/privacy/requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          request_type: requestType,
          details,
          consent_types: requestType === 'consent_withdrawal' ? consentTypes : undefined,
        }),
      });
      if (!res.ok) throw new Error('Failed');
      setDetails('');
      setConsentTypes([]);
      setMsg({ type: 'ok', text: 'Your request has been submitted. Our team will action it and update the status here.' });
      fetchMine();
    } catch (e) {
      setMsg({ type: 'err', text: 'Could not submit your request. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const selected = REQUEST_TYPES.find((t) => t.v === requestType);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="privacy-requests-page">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1" data-testid="privacy-back-btn" aria-label="Back">
            <ChevronLeft size={22} />
          </button>
          <ShieldCheck size={20} className="text-emerald-600" />
          <h1 className="text-lg font-semibold text-gray-800">My Data &amp; Privacy</h1>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6 pb-20">
        <form onSubmit={submit} className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4" data-testid="privacy-request-form">
          <p className="text-sm text-gray-600">
            Submit a request about your personal data. We log every request and respond as required by law.
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Request type</label>
            <select
              value={requestType}
              onChange={(e) => setRequestType(e.target.value)}
              data-testid="privacy-request-type"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              {REQUEST_TYPES.map((t) => (
                <option key={t.v} value={t.v}>{t.label}</option>
              ))}
            </select>
            {selected && <p className="mt-1 text-xs text-gray-500">{selected.help}</p>}
          </div>

          {requestType === 'consent_withdrawal' && (
            <div data-testid="consent-types">
              <label className="block text-sm font-medium text-gray-700 mb-1">Which consent(s)?</label>
              <div className="flex flex-wrap gap-3">
                {CONSENT_OPTIONS.map((c) => (
                  <label key={c} className="flex items-center gap-1.5 text-sm text-gray-700">
                    <input type="checkbox" checked={consentTypes.includes(c)} onChange={() => toggleConsent(c)} data-testid={`consent-${c}`} />
                    <span className="capitalize">{c}</span>
                  </label>
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-500">Leave all unchecked to withdraw all consents.</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Details (optional)</label>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={3}
              data-testid="privacy-request-details"
              placeholder="Add any context that helps us action your request."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          {msg && (
            <div data-testid="privacy-request-msg" className={`text-sm rounded-lg px-3 py-2 ${msg.type === 'ok' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
              {msg.text}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="privacy-request-submit"
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-60"
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            Submit request
          </button>

          {policy && (policy.grievance?.email || policy.privacy_url) && (
            <p className="text-xs text-gray-500 pt-1">
              Current privacy notice: {policy.privacy_version}.{' '}
              {policy.privacy_url && (
                <a href={policy.privacy_url} target="_blank" rel="noreferrer" className="underline">Read notice</a>
              )}
              {policy.grievance?.email && <> · Grievance: {policy.grievance.email}</>}
            </p>
          )}
        </form>

        {policy && (
          <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-2" data-testid="privacy-contact-card">
            <h2 className="text-base font-semibold text-gray-800">Contact &amp; response times</h2>
            {policy.legal_entity_name && (
              <p className="text-sm text-gray-600" data-testid="privacy-operated-by">
                Operated by <span className="font-medium">{policy.legal_entity_name}</span>
                {policy.brand_name && <> (brand: {policy.brand_name})</>}.
              </p>
            )}
            <div className="text-sm text-gray-600 space-y-0.5">
              {policy.grievance?.email && (
                <p data-testid="privacy-grievance">
                  Grievance Officer: <a href={`mailto:${policy.grievance.email}`} className="underline">{policy.grievance.email}</a>
                  {policy.grievance.phone && <> · {policy.grievance.phone}</>}
                </p>
              )}
              {policy.dpo?.email && (
                <p data-testid="privacy-dpo">
                  Data Protection Officer: <a href={`mailto:${policy.dpo.email}`} className="underline">{policy.dpo.email}</a>
                </p>
              )}
              {policy.grievance?.postal_address && (
                <p data-testid="privacy-postal">{policy.grievance.postal_address}</p>
              )}
            </div>
            {policy.response_sla && (policy.response_sla.routine || policy.response_sla.critical) && (
              <div className="text-xs text-gray-500 pt-1" data-testid="privacy-sla">
                {policy.response_sla.routine && <p>Routine requests: {policy.response_sla.routine}.</p>}
                {policy.response_sla.critical && <p>Critical/emergency: {policy.response_sla.critical}.</p>}
              </div>
            )}
            {policy.retention_months != null && (
              <p className="text-xs text-gray-500" data-testid="privacy-retention">
                Default data retention: {policy.retention_months} months.{' '}
                {policy.retention_url && <a href={policy.retention_url} target="_blank" rel="noreferrer" className="underline">Retention policy</a>}
              </p>
            )}
            {policy.minor_policy && (
              <p className="text-xs text-gray-400 pt-1" data-testid="privacy-minor-policy">{policy.minor_policy}</p>
            )}
          </div>
        )}

        <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="privacy-requests-list">
          <h2 className="text-base font-semibold text-gray-800 mb-3">Your requests</h2>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 size={16} className="animate-spin" /> Loading…</div>
          ) : items.length === 0 ? (
            <p className="text-sm text-gray-500">No requests yet.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {items.map((it) => (
                <li key={it.id} className="py-3 flex items-start justify-between gap-3" data-testid={`privacy-request-row-${it.id}`}>
                  <div>
                    <div className="text-sm font-medium text-gray-800 capitalize">{(it.request_type || '').replace('_', ' ')}</div>
                    {it.details && <div className="text-xs text-gray-500 mt-0.5">{it.details}</div>}
                    <div className="text-[11px] text-gray-400 mt-0.5">{new Date(it.created_at).toLocaleString()}</div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full border whitespace-nowrap ${STATUS_COLORS[it.status] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                    {(it.status || '').replace('_', ' ')}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
